import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListSendOutQuery } from "store/services/sendOutApi";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";

const DriverSendOutListScreen = () => {
  const { loggedInUser, isDriver } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Send Outs" }];

  const columns = [
    {
      name: "Send Out ID",
      selector: (row) => row?.sendOutId,
      cell: (row) => {
        const displayId = row?.sendOutId;
        const path = !isDriver
          ? `/app/crm/sendOut/${row?._id}/detail`
          : `/app/driver/sendOut/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Shipment",
      selector: (row) => row.shipmentrequest,
      cell: (row) => {
        const formatShipmentId = (shipmentId) =>
          shipmentId.replace(/(.{4})/g, "$1-").slice(0, -1);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [anchorEl, setAnchorEl] = React.useState(null);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selectedShipments, setSelectedShipments] = React.useState([]);

        const shipments = Array.isArray(row.shipmentrequest)
          ? row.shipmentrequest
          : [];
        const visibleShipments = shipments.slice(0, 2);
        const remainingCount = shipments.length - visibleShipments.length;

        const handleChipClick = (event, allShipments) => {
          setAnchorEl(event.currentTarget);
          setSelectedShipments(allShipments);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        const open = Boolean(anchorEl);

        return shipments.length > 0 ? (
          <>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
              {visibleShipments.map((shipment, index) => (
                <span key={index} style={{ fontSize: "0.9rem" }}>
                  {shipment.IsReturning
                    ? `${formatShipmentId(shipment.shipmentId)} (R)`
                    : formatShipmentId(shipment.shipmentId)}
                  {index !== visibleShipments.length - 1 && ","}
                </span>
              ))}

              {remainingCount > 0 && (
                <Tooltip title="Click to view all Shipment IDs">
                  <Chip
                    label={`+${remainingCount}`}
                    size="small"
                    sx={{
                      marginLeft: "4px",
                      height: "18px",
                      fontSize: "10px",
                      backgroundColor: "#e0e0e0",
                      color: "#333",
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleChipClick(e, shipments)}
                  />
                </Tooltip>
              )}
            </Box>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box p={2} minWidth={100}>
                {selectedShipments.map((shipment, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {shipment.IsReturning
                      ? `${formatShipmentId(shipment.shipmentId)} (R)`
                      : formatShipmentId(shipment.shipmentId)}
                  </Typography>
                ))}
              </Box>
            </Popover>
          </>
        ) : (
          "N/A"
        );
      },
    },
    ...(!isDriver
      ? [
          {
            name: "Send Out Type",
            selector: (row) =>
              row?.responsibleUser
                ? "User Send Out"
                : row?.driver || row?.contractor
                ? "Driver Send Out"
                : "N/A",
            cell: (row) => (
              <div>
                {row?.responsibleUser
                  ? "User Send Out"
                  : row?.driver || row?.contractor
                  ? "Driver Send Out"
                  : "N/A"}
              </div>
            ),
          },
        ]
      : []),
    {
      name: "Created By",
      selector: (row) => row.createdBy?.name || "N/A",
      cell: (row) => row.createdBy?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              !isDriver
                ? `/app/crm/sendOut/${row?._id}/detail`
                : `/app/driver/sendOut/${row?._id}/detail`
            }
          >
            <IconButton size="small" aria-label="View SendOut" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          {!isDriver && (
            <Link to={`/app/crm/sendOut/${row?._id}/edit`}>
              <IconButton size="small" aria-label="Edit SendOut">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
          )}
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchSendOuts, { data: sendOutRes, isLoading, isFetching, error }] =
    useLazyListSendOutQuery();

  React.useEffect(() => {
    fetchSendOuts({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      driverId: loggedInUser.typeRef,
    });
  }, [currentPage, fetchSendOuts, keyword, perPage, loggedInUser]);

  useAppMeta(`Send Outs`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <TableFilter
          searchLabel="Send Out ID/Shipment ID/Driver ID"
          disabled={isLoading || isFetching}
        ></TableFilter>
        <AppTable
          columns={columns}
          data={sendOutRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={sendOutRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DriverSendOutListScreen;

const PageActions = () => {
  const { isDriver } = useAuth();
  return (
    <>
      {!isDriver && (
        <Link to="/app/crm/sendOut/create">
          <Button startIcon={<Add />} variant="contained" color="primary">
            Add Send Out
          </Button>
        </Link>
      )}
    </>
  );
};
